import { AMSUITE_EMPTY_GUID } from '@neworg-net/amsuite-core/apps';
import { NotificationAction, NotificationItem, NotifierNotificationDto } from './notification.model';

function record(value: unknown): Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Readonly<Record<string, unknown>>
    : {};
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function identifier(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return String(value);
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const id = value.trim();
  return id.toLowerCase() === AMSUITE_EMPTY_GUID ? undefined : id;
}

function utcDate(value: unknown): string | undefined {
  const input = text(value);
  // SQL DATETIME2 perde il DateTimeKind: il JSON di *AtUtc può non contenere "Z".
  // Solo l'ISO senza offset riceve UTC; gli offset espliciti restano invariati.
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/u.test(input) ? `${input}Z` : input;
  return normalized && Number.isFinite(Date.parse(normalized)) ? normalized : undefined;
}

function numericId(value: unknown): number | undefined {
  const id = typeof value === 'string' && /^\d+$/u.test(value) ? Number(value) : value;
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0 ? id : undefined;
}

function metadata(value: unknown): Readonly<Record<string, unknown>> {
  if (typeof value !== 'string') return record(value);
  try {
    return record(JSON.parse(value));
  } catch {
    // Metadati storici incompleti: la riga resta leggibile e apre l'app di origine.
    return {};
  }
}

function actionFromMetadata(data: Readonly<Record<string, unknown>>): NotificationAction {
  const requestId = identifier(data['requestId'] ?? data['RequestId'] ?? data['dialogRequestId']);
  if (requestId) return { kind: 'dialog', requestId };

  const objectId = identifier(data['objectId'] ?? data['ObjectId']);
  switch (data['type'] ?? data['Type']) {
    case 'Workflow':
      if (objectId) return { kind: 'activity', objectId };
      break;
    case 'WorkflowProject':
      if (objectId) return { kind: 'project', objectId };
      break;
  }

  const newsId = identifier(data['NewsId'] ?? data['newsId']);
  const categoryId = identifier(data['CategoryId'] ?? data['categoryId']);
  return newsId && categoryId ? { kind: 'news', newsId, categoryId } : { kind: 'application' };
}

function legacyAction(type: unknown, object: unknown): NotificationAction {
  const objectId = identifier(object);
  if (!objectId) return { kind: 'application' };
  switch (Number(type)) {
    case 1: return { kind: 'dialog', requestId: objectId };
    case 2: return { kind: 'activity', objectId };
    case 3: return { kind: 'project', objectId };
    default: return { kind: 'application' };
  }
}

/** Payload REST legacy: campi camelCase, diverso dall'evento SignalR 2.x. */
export function normalizeLegacyRest(payload: unknown): NotificationItem | null {
  const row = record(payload);
  const id = numericId(row['id']);
  if (!id) return null;
  const message = record(row['message']);
  return {
    source: 'legacy', id, key: `legacy:${id}`,
    messageId: identifier(message['id']),
    appId: text(message['appId']),
    title: text(message['subject']), body: text(message['text']),
    isUnread: row['status'] === 0,
    createdAtUtc: utcDate(row['createdAtUtc'] ?? message['createdAtUtc']),
    dataOwnerId: identifier(row['dataOwnerId'] ?? message['dataOwnerId']),
    metadata: {}, action: legacyAction(message['type'], message['objectId']),
  };
}

/** Payload evento legacy: campi PascalCase. Non aggiorna mai il conteggio persistito. */
export function normalizeLegacyEvent(payload: unknown): NotificationItem | null {
  const row = record(payload);
  const id = numericId(row['Id']);
  if (!id) return null;
  const message = record(row['Message']);
  return {
    source: 'legacy', id, key: `legacy:${id}`,
    messageId: identifier(message['Id']),
    appId: text(message['AppId']),
    title: text(message['Subject']), body: text(message['Text']),
    isUnread: row['Status'] === 0,
    createdAtUtc: utcDate(row['CreatedAtUtc'] ?? message['CreatedAtUtc']),
    dataOwnerId: identifier(row['DataOwnerId'] ?? message['DataOwnerId']),
    metadata: {}, action: legacyAction(message['Type'], message['ObjectId']),
  };
}

export function normalizeNotifierDto(dto: NotifierNotificationDto): NotificationItem {
  const data = metadata(dto.metadata);
  return {
    source: 'notifier', id: dto.id, key: `notifier:${dto.id}`,
    messageId: dto.messageId, appId: dto.appId,
    title: dto.title, body: dto.body, isUnread: !dto.readAtUtc,
    createdAtUtc: utcDate(dto.createdAtUtc), dataOwnerId: identifier(dto.dataOwnerId),
    metadata: data, action: actionFromMetadata(data),
  };
}

export function uniqueNotifications(items: readonly NotificationItem[]): NotificationItem[] {
  return [...new Map(items.map(item => [item.key, item])).values()];
}

/** Date reali in ordine decrescente; senza data resta l'ordine stabile della sorgente. */
export function sortNotifications(items: readonly NotificationItem[]): NotificationItem[] {
  return [...items].sort((left, right) => {
    if (!left.createdAtUtc && !right.createdAtUtc) return 0;
    if (!left.createdAtUtc) return 1;
    if (!right.createdAtUtc) return -1;
    return Date.parse(right.createdAtUtc) - Date.parse(left.createdAtUtc);
  });
}
